import * as React from "react";

const PricingComparison = () => {
  const [pinCount, setPinCount] = React.useState(200);
  
  // Calculate prices based on pin count
  const calculatePrices = (pins: number) => {
    const ourPrice = pins <= 50 ? 0 : 
                    pins <= 200 ? 7.99 :
                    pins <= 500 ? 19.99 :
                    29.99;
                    
    const competitorPrice = pins <= 100 ? 29.99 :
                           pins <= 500 ? 49.99 :
                           99.99;
                           
    return {
      ourPrice: Number(ourPrice).toFixed(2),
      competitorPrice: Number(competitorPrice).toFixed(2),
      savings: Number(competitorPrice - ourPrice).toFixed(2)
    };
  };

  const prices = calculatePrices(pinCount);

  const ourFeatures = [
    "Unlimited AI Generation",
    "Automated Scheduling",
    "Direct Shopify Integration"
  ];

  const theirFeatures = [
    "Limited AI Credits",
    "Manual Scheduling",
    "No Direct Integration"
  ];

  return (
    <div className="max-w-4xl mx-auto mt-12">
      {/* Header */}
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-gray-900">
          Compare pricing with competitors
        </h2>
        <p className="mt-2 text-base text-gray-600">
          See how much you can save with Boosted Pins
        </p>
      </div>

      {/* Slider Section */}
      <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200 mb-8">
        <h3 className="text-lg font-medium text-gray-900 mb-6">
          How many pins do you need per month?
        </h3>
        
        <div className="max-w-2xl mx-auto">
          {/* Pin Count Display */}
          <div className="text-center mb-4">
            <span className="text-3xl font-bold text-gray-900">
              {pinCount === 1000 ? "Unlimited" : pinCount}
            </span>
            <span className="text-gray-600 ml-2">pins/month</span>
          </div>

          {/* Slider */}
          <div className="relative">
            <input
              type="range"
              min="50"
              max="1000"
              step="50"
              value={pinCount}
              onChange={(e) => setPinCount(Number(e.target.value))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              style={{
                background: `linear-gradient(to right, rgb(37 99 235) ${(pinCount - 50) / (1000 - 50) * 100}%, rgb(229 231 235) ${(pinCount - 50) / (1000 - 50) * 100}%)`
              }}
            />
            
            {/* Tick marks */}
            <div className="flex justify-between text-xs text-gray-500 mt-2">
              <span>50</span>
              <span>250</span>
              <span>500</span>
              <span>750</span>
              <span>Unlimited</span>
            </div>
          </div>
        </div>
      </div>

      {/* Pricing Cards */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Our Price */}
        <div className="bg-white p-6 rounded-lg shadow-sm border-2 border-blue-200 relative">
          <div className="absolute -top-3 left-4">
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              RECOMMENDED
            </span>
          </div>
          
          <div className="flex justify-between items-start mb-4">
            <div>
              <h3 className="text-lg font-medium text-gray-900">Boosted Pins</h3>
              <p className="text-sm text-gray-500">Automated Pinterest marketing</p>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-gray-900">
                ${prices.ourPrice}
                <span className="text-sm font-normal text-gray-500 ml-1">/month</span>
              </div>
            </div>
          </div>

          <ul className="space-y-3 mb-6">
            {ourFeatures.map((feature, index) => (
              <li key={index} className="flex items-center gap-2 text-gray-700">
                <svg className="w-5 h-5 text-green-500 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                {feature}
              </li>
            ))}
          </ul>

          <button onClick={() => {
            const pricingV2Main = document.getElementById('pricing');
            pricingV2Main?.scrollIntoView({ behavior: 'smooth' });
          }} className="w-full bg-[#E60023] text-white px-4 py-2 rounded-md hover:bg-[#BD081C] transition-colors">
            Get started
          </button>
        </div>

        {/* Competitor Price */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h3 className="text-lg font-medium text-gray-900">Competition</h3>
              <p className="text-sm text-gray-500">Traditional Pinterest tools</p>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-gray-900">
                ${prices.competitorPrice}
                <span className="text-sm font-normal text-gray-500 ml-1">/month</span>
              </div>
            </div>
          </div>

          <ul className="space-y-3 mb-6">
            {theirFeatures.map((feature, index) => (
              <li key={index} className="flex items-center gap-2 text-gray-500">
                <svg className="w-5 h-5 text-red-400 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                {feature}
              </li>
            ))}
          </ul>

          <button disabled className="w-full bg-gray-100 text-gray-400 px-4 py-2 rounded-md cursor-not-allowed">
            Not available
          </button>
        </div>
      </div>

      {/* Savings Banner */}
      {Number(prices.savings) > 0 && (
        <div className="mt-8 p-4 bg-green-50 border border-green-200 rounded-lg text-center">
          <span className="text-green-800 font-medium">
            Save ${prices.savings} per month with Boosted Pins
          </span>
        </div>
      )}
    </div>
  );
};

export default PricingComparison; 